import { Box, Flex, Text } from '@chakra-ui/react'

import {
  GreyGrey10,
  GreyGrey70,
  GreyGrey90,
  Lato13Bold,
  Lato13Regular
} from '@sequencehq/design-tokens'
import { SwitchLineInput, TextField, Button } from '@sequencehq/core-components'
import { PlusIcon, TrashIcon } from '@heroicons/react/16/solid'

import { UseQuoteSettingsInterface } from 'modules/Cube/settings/QuoteSettings/useQuoteSettings'
import { LogoDropboxSign } from 'modules/Cube/settings/QuoteSettings/LogoDropboxSign'
import { useQuoteSettingsForm } from 'Cube/settings/QuoteSettings/useQuoteSettingsForm'
import { Label } from '@sequencehq/forms'

export const QuoteSettingsForm = ({
  hook
}: {
  hook: UseQuoteSettingsInterface
}) => {
  const { fieldsConfig, submit, isDirty, isSubmitting } = useQuoteSettingsForm({
    fieldValues: {
      isSignaturesEnabled: hook.quoteSettings.isSignaturesEnabled,
      isInternalSignerEnabled: Boolean(
        hook.quoteSettings.counterSigners?.length
      ),
      counterSigners: hook.quoteSettings.counterSigners || []
    },
    onSubmit: hook.functions.onSubmit
  })

  const showSignerFields = fieldsConfig.isInternalSignerEnabled.visible

  return (
    <Flex maxWidth="640px">
      <Flex padding="24px" gap="24px" flexDirection="column">
        <Flex gap="8px" flexDirection="column">
          <Text {...Lato13Bold} color={GreyGrey90}>
            E-signatures
          </Text>

          <Flex gap="4px" flexDirection="column">
            <SwitchLineInput
              value={fieldsConfig.isSignaturesEnabled.value}
              onChange={fieldsConfig.isSignaturesEnabled.onChange}
              text={
                <Text {...Lato13Bold} color={GreyGrey90}>
                  Enable e-signatures
                </Text>
              }
              styles={{ label: { marginLeft: '0px' } }}
            />

            <Flex flexDirection="column" gap="8px" marginLeft="36px">
              <Text {...Lato13Regular} color={GreyGrey70}>
                Enable this setting if you want to allow customers to sign
                quotes digitally.
              </Text>

              <Flex
                paddingX="12px"
                paddingY="16px"
                background={GreyGrey10}
                borderRadius="8px"
                justifyContent="space-between"
              >
                <Text {...Lato13Regular} color={GreyGrey70}>
                  Powered by
                </Text>
                <Box position="relative" top="2px">
                  <LogoDropboxSign />
                </Box>
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        {showSignerFields && (
          <Flex gap="4px" flexDirection="column">
            <SwitchLineInput
              value={Boolean(fieldsConfig.isInternalSignerEnabled.value)}
              onChange={fieldsConfig.isInternalSignerEnabled.onChange}
              isDisabled={fieldsConfig.isInternalSignerEnabled.disabled}
              text={
                <Text {...Lato13Bold} color={GreyGrey90}>
                  Enable countersigner
                </Text>
              }
              styles={{ label: { marginLeft: '0px' } }}
            />

            <Box marginLeft="36px">
              <Text {...Lato13Regular} color={GreyGrey70}>
                Include an internal person (e.g. your CEO) as a quote counter
                signer. You can add and remove internal signers while editing a
                quote.
              </Text>
            </Box>
          </Flex>
        )}

        <Flex flexDirection="column" marginLeft="36px" gap={0}>
          {fieldsConfig.counterSigners.visible &&
            fieldsConfig.counterSigners.value.map((signer, index) => (
              <Flex key={index} gap={2} alignItems="flex-start">
                <Box flex={1}>
                  {index === 0 && <Label>Name</Label>}
                  <TextField
                    value={signer.name}
                    onChange={value => {
                      const updatedSigners = [
                        ...fieldsConfig.counterSigners.value
                      ]
                      updatedSigners[index] = { ...signer, name: value }
                      fieldsConfig.counterSigners.onChange(updatedSigners)
                    }}
                    placeholder="John Doe"
                  />
                </Box>
                <Box flex={1}>
                  {index === 0 && <Label>Email</Label>}
                  <TextField
                    value={signer.email}
                    onChange={value => {
                      const updatedSigners = [
                        ...fieldsConfig.counterSigners.value
                      ]
                      updatedSigners[index] = { ...signer, email: value }
                      fieldsConfig.counterSigners.onChange(updatedSigners)
                    }}
                    placeholder="john@acme.com"
                  />
                </Box>
                {index === 0 ? (
                  <Box minWidth="40px" height="40px" />
                ) : (
                  fieldsConfig.counterSigners.deleteAvailable && (
                    <Button
                      aria-label="Remove signer"
                      icon={<TrashIcon width={16} height={16} />}
                      onClick={() =>
                        fieldsConfig.counterSigners.onRemove(index)
                      }
                      variant="ghost"
                      size="40px"
                    />
                  )
                )}
              </Flex>
            ))}

          {fieldsConfig.counterSigners.visible && (
            <Flex>
              <Button
                variant="ghost"
                leadingIcon={
                  <PlusIcon width="16px" height="16px" color="inherit" />
                }
                onClick={fieldsConfig.counterSigners.onAddNew}
                disabled={fieldsConfig.counterSigners.value.length >= 5}
              >
                Add internal signer
              </Button>
            </Flex>
          )}
        </Flex>

        <Flex>
          <Button disabled={!isDirty} onClick={() => void submit()}>
            Save changes
          </Button>
        </Flex>
      </Flex>
    </Flex>
  )
}
